import { Component, OnInit, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { Party } from '../../objects/party';
import { PartyService } from '../../service/entity-services/party.service';

@Component({
  selector: 'app-vendor-connections',
  templateUrl: './vendor-connections.component.html',
  styleUrls: ['./vendor-connections.component.css'],
  standalone: true,
  imports: [CommonModule]
})
export class VendorConnectionsComponent implements OnInit {
  private partyService = inject(PartyService);
  private router = inject(Router);

  // Modern Angular signals for reactive state
  vendorObjectArray = signal<Party[]>([]);
  waiting = signal<boolean>(true);

  // Grid configuration
  private gridApi: any;

  defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true
  };

  columnDefs = [
    { headerName: 'Party Name', field: 'name', width: 75 },
    { headerName: 'Address', field: 'address', width: 125 },
    { headerName: 'Parent Party', field: 'parentVendorFriendlyName', width: 100 },
    { headerName: 'Description', field: 'description', width: 100 }
  ];

  @Input() selectedVendorList: any[] = [];
  @Input() businessAssociateName?: string;
  @Input() insuredParty?: string;
  @Input() parentParty?: string;
  @Input() partyProvidingCoi?: string;
  @Input() partiesTabOnScreen?: boolean;
  @Input() directVendor?: string;
  @Input() parentVendor?: string;

  ngOnInit(): void {
    this.hydrateVendorData();
  }

  /**
   * Hydrates vendor data from input properties.
   * Handles multiple vendor input sources: directVendor, parentVendor, selectedVendorList
   */
  private async hydrateVendorData(): Promise<void> {
    this.waiting.set(true);
    const parties: Party[] = [];

    try {
      // Collect all vendor IDs from various input sources
      const vendorIds: string[] = [];

      if (this.directVendor) {
        vendorIds.push(this.directVendor);
      }

      if (this.parentVendor) {
        vendorIds.push(this.parentVendor);
      }

      if (this.businessAssociateName) {
        vendorIds.push(this.businessAssociateName);
      }

      if (this.insuredParty) {
        vendorIds.push(this.insuredParty);
      }

      if (this.partyProvidingCoi) {
        vendorIds.push(this.partyProvidingCoi);
      }

      if (this.selectedVendorList && this.selectedVendorList.length > 0) {
        const listIds = this.selectedVendorList.map(vendor =>
          typeof vendor === 'object' && vendor?.vendorNum ? vendor.vendorNum : vendor
        );
        vendorIds.push(...listIds);
      }

      // Remove duplicates using native Set (no lodash needed!)
      const uniqueVendorIds = Array.from(new Set(vendorIds)).filter(id => !!id);

      // Fetch each party (vendor) using modern firstValueFrom instead of deprecated toPromise
      for (const vendorId of uniqueVendorIds) {
        const party = await firstValueFrom(this.partyService.getPartyObjectFullyHydrated(vendorId));
        if (party) {
          parties.push(party);
        }
      }

      this.vendorObjectArray.set(parties);
    } catch (error) {
      console.error('Error hydrating vendor data:', error);
    } finally {
      this.waiting.set(false);
    }
  }

  /**
   * Handle cell click events in the grid
   */
  onCellClicked(event: any): void {
    if (event?.data?.vendorNum) {
      this.router.navigate(['/securehome/vendor', event.data.vendorNum]);
    }
  }

  /**
   * Initialize grid when ready
   */
  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  /**
   * Refresh grid data
   */
  refreshGridData(): void {
    if (this.gridApi) {
      this.gridApi.setRowData(this.vendorObjectArray());
    }
  }
}
